import { useCallback, useState, type MouseEvent, type ReactNode } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

export interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Paints the confirm button as destructive and says so out loud. */
  destructive?: boolean;
  /**
   * Apaga «confirmar» mientras lo que el diálogo tiene que decir no está listo.
   *
   * Existe para la confirmación que CUENTA antes de preguntar —eliminar a una
   * persona dice cuánto se va con ella—: mientras esa cuenta carga, o si no se
   * pudo hacer, aceptar sería confirmar algo que el diálogo todavía no dijo.
   * «Cancelar» sigue funcionando siempre.
   */
  confirmDisabled?: boolean;
}

export interface ConfirmDialogProps extends ConfirmOptions {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
}

/**
 * Controlled confirmation. Prefer `useConfirm()` unless the trigger already
 * owns open state for other reasons.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  destructive = false,
  confirmDisabled = false,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={busy ? undefined : onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {/* Una frase va en el párrafo de siempre. Cuando lo que hay que decir
              tiene estructura —la lista de lo que se borra con una persona—, el
              párrafo no puede envolverlo: una lista dentro de un `<p>` es HTML
              inválido y el navegador la saca afuera. Ahí la descripción es un
              bloque, con el mismo estilo y el mismo `aria-describedby`. */}
          {description &&
            (typeof description === 'string' ? (
              <AlertDialogDescription>{description}</AlertDialogDescription>
            ) : (
              <AlertDialogDescription asChild>
                <div>{description}</div>
              </AlertDialogDescription>
            ))}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy || confirmDisabled}
            onClick={(event: MouseEvent) => {
              event.preventDefault();
              void run();
            }}
            className={cn(
              destructive && 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
            )}
          >
            {busy && <Spinner className="size-3.5" />}
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

interface PendingConfirm extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

export interface UseConfirmResult {
  /** `await confirm({ … })` resolves true only if the user pressed confirm. */
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  /** Render this anywhere in the subtree; it is inert until `confirm` is called. */
  dialog: ReactNode;
}

/**
 * Promise-shaped confirmation, so a handler reads top to bottom:
 * `if (!(await confirm({ title: '¿Eliminar lead?' }))) return;`
 */
export function useConfirm(): UseConfirmResult {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setPending({ ...options, resolve });
      }),
    [],
  );

  const settle = useCallback(
    (value: boolean) => {
      setPending((current) => {
        current?.resolve(value);
        return null;
      });
    },
    [],
  );

  const dialog = pending ? (
    <ConfirmDialog
      open
      onOpenChange={(next) => {
        if (!next) settle(false);
      }}
      onConfirm={() => settle(true)}
      title={pending.title}
      description={pending.description}
      confirmLabel={pending.confirmLabel}
      cancelLabel={pending.cancelLabel}
      destructive={pending.destructive}
    />
  ) : null;

  return { confirm, dialog };
}
